import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Square, Drum, Music, Piano } from 'lucide-react';
import { useSoundfont } from '../hooks/useSoundfont';

const freqToMidi = (freq) => Math.round(12 * Math.log2(freq / 440) + 69);

// Web Audio backing track: drums + bass + piano synthesizers driven by a
// shared scheduler. Loops through the song's chord progression at the
// given BPM. Each chord lasts one bar (4 beats / 8 eighth-note steps).
// The guitarist mutes any instrument they want to play themselves.

const NOTE_FREQ = {
  C: 65.41, 'C#': 69.30, Db: 69.30,
  D: 73.42, 'D#': 77.78, Eb: 77.78,
  E: 82.41, F: 87.31, 'F#': 92.50, Gb: 92.50,
  G: 98.00, 'G#': 103.83, Ab: 103.83,
  A: 110.00, 'A#': 116.54, Bb: 116.54,
  B: 123.47,
};

const parseChord = (chordName) => {
  const m = chordName.match(/^([A-G])(#|b)?(.*)$/);
  if (!m) return null;
  const [, letter, accidental = '', quality = ''] = m;
  const root = letter + accidental;
  const rootFreq = NOTE_FREQ[root] || NOTE_FREQ[letter];

  let intervals = [0, 4, 7];
  const isMinor = /^m(?!aj)/.test(quality);
  if (isMinor) intervals = [0, 3, 7];

  if (/maj7/.test(quality)) {
    intervals = isMinor ? [0, 3, 7, 11] : [0, 4, 7, 11];
  } else if (isMinor && /7/.test(quality)) {
    intervals = [0, 3, 7, 10];
  } else if (/7/.test(quality)) {
    intervals = [0, 4, 7, 10];
  } else if (/sus4/.test(quality)) {
    intervals = [0, 5, 7];
  } else if (/sus2/.test(quality)) {
    intervals = [0, 2, 7];
  } else if (/dim/.test(quality)) {
    intervals = [0, 3, 6];
  } else if (/aug/.test(quality)) {
    intervals = [0, 4, 8];
  }

  if (/9/.test(quality) && !/maj7|m7/.test(quality)) {
    if (!intervals.includes(10)) intervals.push(10);
    intervals.push(14);
  } else if (/9/.test(quality)) {
    intervals.push(14);
  }

  return { rootFreq, intervals };
};

const semitoneFreq = (baseFreq, semitones) => baseFreq * Math.pow(2, semitones / 12);

const playKick = (ctx, when, out) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g); g.connect(out);
  o.frequency.setValueAtTime(150, when);
  o.frequency.exponentialRampToValueAtTime(0.01, when + 0.5);
  g.gain.setValueAtTime(1, when);
  g.gain.exponentialRampToValueAtTime(0.01, when + 0.5);
  o.start(when); o.stop(when + 0.5);
};

const playSnare = (ctx, when, out, noiseBuf) => {
  const o = ctx.createOscillator();
  const og = ctx.createGain();
  o.type = 'triangle';
  o.connect(og); og.connect(out);
  o.frequency.setValueAtTime(100, when);
  og.gain.setValueAtTime(0.7, when);
  og.gain.exponentialRampToValueAtTime(0.01, when + 0.1);
  o.start(when); o.stop(when + 0.1);
  const n = ctx.createBufferSource();
  n.buffer = noiseBuf;
  const f = ctx.createBiquadFilter();
  f.type = 'highpass'; f.frequency.value = 1000;
  const ng = ctx.createGain();
  n.connect(f); f.connect(ng); ng.connect(out);
  ng.gain.setValueAtTime(0.5, when);
  ng.gain.exponentialRampToValueAtTime(0.01, when + 0.2);
  n.start(when);
};

const playHihat = (ctx, when, out, noiseBuf) => {
  const n = ctx.createBufferSource();
  n.buffer = noiseBuf;
  const f = ctx.createBiquadFilter();
  f.type = 'highpass'; f.frequency.value = 7000;
  const g = ctx.createGain();
  n.connect(f); f.connect(g); g.connect(out);
  g.gain.setValueAtTime(0.3, when);
  g.gain.exponentialRampToValueAtTime(0.01, when + 0.05);
  n.start(when);
};

const playBass = (ctx, when, out, freq, duration = 0.4) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  const f = ctx.createBiquadFilter();
  o.type = 'sawtooth';
  f.type = 'lowpass';
  f.frequency.value = 600;
  o.connect(f); f.connect(g); g.connect(out);
  o.frequency.setValueAtTime(freq, when);
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(0.6, when + 0.01);
  g.gain.exponentialRampToValueAtTime(0.01, when + duration);
  o.start(when); o.stop(when + duration + 0.05);
};

const playPianoTone = (ctx, when, out, freq, duration = 1.0, vol = 0.18) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'triangle';
  o.connect(g); g.connect(out);
  o.frequency.setValueAtTime(freq, when);
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(vol, when + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, when + duration);
  o.start(when); o.stop(when + duration + 0.05);
};

const PATTERNS = {
  rock: [['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['hihat'], ['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['hihat']],
  pop: [['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['hihat'], ['hihat'], ['kick', 'hihat'], ['snare', 'hihat'], ['hihat']],
  ballad: [['kick', 'hihat'], [], ['snare', 'hihat'], [], ['kick', 'hihat'], [], ['snare', 'hihat'], []],
  folk: [['kick'], ['hihat'], ['snare'], ['hihat'], ['kick'], ['hihat'], ['snare'], ['hihat']],
  blues: [['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['kick', 'hihat'], ['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['hihat']],
  country: [['kick'], ['hihat'], ['snare'], ['hihat'], ['kick'], ['hihat'], ['snare'], ['hihat']],
  funk: [['kick', 'hihat'], ['hihat'], ['hihat'], ['snare', 'hihat'], ['kick', 'hihat'], ['kick', 'hihat'], ['snare', 'hihat'], ['hihat']],
  jazz: [['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['hihat'], ['kick', 'hihat'], ['hihat'], ['snare', 'hihat'], ['hihat']],
};

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_SEC = 0.12;

const meterToStepsPerBar = (sig) => {
  const numerator = parseInt((sig || '4/4').split('/')[0], 10) || 4;
  // 4/4 -> 8 (eighths), 3/4 -> 6, 6/8 -> 6, 12/8 -> 12, 9/8 -> 9, 5/4 -> 10
  if (sig === '6/8') return 6;
  if (sig === '12/8') return 12;
  if (sig === '9/8') return 9;
  return numerator * 2;
};

// Per-meter step positions used by bass + piano styles. rootStep is
// where the root note fires; fifthStep is the second strong pulse;
// beatSteps is the full list of pulses for "alternating" / "walking" /
// "pedal" patterns. For 6/8 + 12/8 these are the dotted-quarter beats.
const METER_STYLE = {
  '4/4':  { rootStep: 0, fifthStep: 4, beatSteps: [0, 2, 4, 6] },
  '3/4':  { rootStep: 0, fifthStep: 4, beatSteps: [0, 2, 4] },
  '6/8':  { rootStep: 0, fifthStep: 3, beatSteps: [0, 3] },
  '12/8': { rootStep: 0, fifthStep: 6, beatSteps: [0, 3, 6, 9] },
};
const getMeterStyle = (sig) => METER_STYLE[sig] || METER_STYLE['4/4'];

const BackingTrack = ({
  bpm: initialBpm = 100,
  chords = [],
  timeSignature = '4/4',
  initialPattern = 'rock',
  bassStyle = 'rootFifth',
  pianoStyle = 'block',
  accent = 'amber',
}) => {
  const STEPS_PER_BAR = meterToStepsPerBar(timeSignature);
  const meterStyle = getMeterStyle(timeSignature);
  const { t } = useTranslation();
  const safeChords = chords && chords.length > 0 ? chords : ['C'];

  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(initialBpm);
  const [patternKey, setPatternKey] = useState(initialPattern);
  const [bassStyleKey, setBassStyleKey] = useState(bassStyle);
  const [pianoStyleKey, setPianoStyleKey] = useState(pianoStyle);
  const [drumsOn, setDrumsOn] = useState(true);
  const [bassOn, setBassOn] = useState(true);
  const [pianoOn, setPianoOn] = useState(true);
  const [volume, setVolume] = useState(0.7);
  const [currentChordIdx, setCurrentChordIdx] = useState(0);

  const [audioCtx, setAudioCtx] = useState(null);
  const { instrument: piano } = useSoundfont('acoustic_grand_piano', audioCtx);
  const { instrument: bass } = useSoundfont('electric_bass_finger', audioCtx);
  const pianoRef = useRef(null);
  const bassRef = useRef(null);
  useEffect(() => { pianoRef.current = piano; }, [piano]);
  useEffect(() => { bassRef.current = bass; }, [bass]);

  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const snareNoiseRef = useRef(null);
  const hihatNoiseRef = useRef(null);
  const isPlayingRef = useRef(false);
  const bpmRef = useRef(initialBpm);
  const patternRef = useRef(initialPattern);
  const bassStyleRef = useRef(bassStyle);
  const pianoStyleRef = useRef(pianoStyle);
  const drumsOnRef = useRef(true);
  const bassOnRef = useRef(true);
  const pianoOnRef = useRef(true);
  const stepsPerBarRef = useRef(STEPS_PER_BAR);
  useEffect(() => { stepsPerBarRef.current = STEPS_PER_BAR; }, [STEPS_PER_BAR]);
  const meterStyleRef = useRef(meterStyle);
  useEffect(() => { meterStyleRef.current = meterStyle; }, [meterStyle]);
  const chordsRef = useRef(safeChords);
  const globalStepRef = useRef(0);
  const nextStepTimeRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { patternRef.current = patternKey; }, [patternKey]);
  useEffect(() => { bassStyleRef.current = bassStyleKey; }, [bassStyleKey]);
  useEffect(() => { pianoStyleRef.current = pianoStyleKey; }, [pianoStyleKey]);
  useEffect(() => { setPatternKey(initialPattern); }, [initialPattern]);
  useEffect(() => { setBassStyleKey(bassStyle); }, [bassStyle]);
  useEffect(() => { setPianoStyleKey(pianoStyle); }, [pianoStyle]);
  useEffect(() => { drumsOnRef.current = drumsOn; }, [drumsOn]);
  useEffect(() => { bassOnRef.current = bassOn; }, [bassOn]);
  useEffect(() => { pianoOnRef.current = pianoOn; }, [pianoOn]);
  useEffect(() => { chordsRef.current = safeChords; }, [safeChords]);
  useEffect(() => { setBpm(initialBpm); }, [initialBpm]);
  useEffect(() => {
    if (masterRef.current) masterRef.current.gain.value = volume;
  }, [volume]);

  const scheduler = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || !isPlayingRef.current) return;
    const pattern = PATTERNS[patternRef.current] || PATTERNS.rock;
    const eighth = 60.0 / bpmRef.current / 2;
    const out = masterRef.current;
    const stepsPerBar = stepsPerBarRef.current;

    while (nextStepTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD_SEC) {
      const t0 = nextStepTimeRef.current;
      const gStep = globalStepRef.current;
      const stepInBar = gStep % stepsPerBar;
      const barIdx = Math.floor(gStep / stepsPerBar);
      const chordIdx = barIdx % chordsRef.current.length;
      const chordName = chordsRef.current[chordIdx];
      const parsed = parseChord(chordName);

      if (drumsOnRef.current) {
        const hits = pattern[stepInBar % pattern.length] || [];
        for (const h of hits) {
          if (h === 'kick') playKick(ctx, t0, out);
          else if (h === 'snare') playSnare(ctx, t0, out, snareNoiseRef.current);
          else if (h === 'hihat') playHihat(ctx, t0, out, hihatNoiseRef.current);
        }
      }

      const playBassNote = (when, freq, dur) => {
        const sf = bassRef.current;
        if (sf) {
          try { sf.play(freqToMidi(freq), when, { destination: out, gain: 1.0, duration: dur }); return; }
          catch (_) { /* fall through */ }
        }
        playBass(ctx, when, out, freq, dur);
      };
      const playPianoNote = (when, freq, dur, vol) => {
        const sf = pianoRef.current;
        if (sf) {
          try { sf.play(freqToMidi(freq), when, { destination: out, gain: 0.65, duration: dur }); return; }
          catch (_) { /* fall through */ }
        }
        playPianoTone(ctx, when, out, freq, dur, vol);
      };

      if (parsed) {
        const m = meterStyleRef.current;
        const beatIdx = m.beatSteps.indexOf(stepInBar);
        const halfBarSteps = m.fifthStep;
        const beatsLeft = m.beatSteps.length - 1 - beatIdx;

        if (bassOnRef.current) {
          const fifth = parsed.intervals.includes(7) ? 7 : 0;
          const third = parsed.intervals.find(i => i === 3 || i === 4) || 0;
          const seventh = parsed.intervals.find(i => i === 10 || i === 11) || 12;
          const bassStyleK = bassStyleRef.current;
          if (bassStyleK === 'rootFifth') {
            if (stepInBar === m.rootStep) playBassNote(t0, parsed.rootFreq, eighth * halfBarSteps);
            else if (stepInBar === m.fifthStep) playBassNote(t0, semitoneFreq(parsed.rootFreq, fifth), eighth * (stepsPerBar - halfBarSteps));
          } else if (bassStyleK === 'alternating') {
            if (beatIdx >= 0) {
              const semis = beatIdx % 2 === 0 ? 0 : fifth;
              const dur = eighth * Math.max(1, Math.floor(stepsPerBar / m.beatSteps.length));
              playBassNote(t0, semitoneFreq(parsed.rootFreq, semis), dur);
            }
          } else if (bassStyleK === 'walking') {
            if (beatIdx >= 0) {
              const walkTones = [0, third, fifth, seventh < 12 ? seventh : seventh - 12];
              const semis = walkTones[beatIdx % walkTones.length];
              const dur = eighth * Math.max(1, Math.floor(stepsPerBar / m.beatSteps.length));
              playBassNote(t0, semitoneFreq(parsed.rootFreq, semis), dur);
            }
          } else if (bassStyleK === 'pedal') {
            if (beatIdx >= 0) {
              const dur = eighth * Math.max(1, Math.floor(stepsPerBar / m.beatSteps.length));
              playBassNote(t0, parsed.rootFreq, dur);
            }
          }
        }
        if (pianoOnRef.current) {
          const barSeconds = eighth * stepsPerBar;
          const pianoStyleK = pianoStyleRef.current;
          if (pianoStyleK === 'block') {
            if (stepInBar === m.rootStep) {
              for (const semis of parsed.intervals) {
                playPianoNote(t0, semitoneFreq(parsed.rootFreq, semis + 24), barSeconds * 0.95, 0.12);
              }
            } else if (stepInBar === m.fifthStep) {
              for (const semis of parsed.intervals) {
                playPianoNote(t0, semitoneFreq(parsed.rootFreq, semis + 24), eighth * (stepsPerBar - halfBarSteps - 1), 0.08);
              }
            }
          } else if (pianoStyleK === 'arpeggio') {
            const tones = parsed.intervals;
            const semis = tones[stepInBar % tones.length];
            playPianoNote(t0, semitoneFreq(parsed.rootFreq, semis + 24), eighth * 1.5, 0.11);
          } else if (pianoStyleK === 'comping') {
            // Chord stab on every beat AFTER the first (offbeat-ish jazz feel).
            if (beatIdx > 0) {
              for (const semis of parsed.intervals) {
                playPianoNote(t0, semitoneFreq(parsed.rootFreq, semis + 24), eighth * 1.5, 0.13);
              }
            }
          } else if (pianoStyleK === 'waltz') {
            // Low root on beat 1, chord on subsequent beats.
            if (stepInBar === m.rootStep) {
              playPianoNote(t0, semitoneFreq(parsed.rootFreq, 24), eighth * stepsPerBar, 0.10);
            } else if (beatIdx > 0) {
              for (const semis of parsed.intervals) {
                playPianoNote(t0, semitoneFreq(parsed.rootFreq, semis + 24), eighth * 1.8, 0.11);
              }
            }
          }
        }
      }

      if (stepInBar === 0) setCurrentChordIdx(chordIdx);

      globalStepRef.current = gStep + 1;
      nextStepTimeRef.current = t0 + eighth;
    }
  }, []);

  const start = useCallback(() => {
    if (!ctxRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      setAudioCtx(ctx);
      masterRef.current = ctx.createGain();
      masterRef.current.gain.value = volume;
      masterRef.current.connect(ctx.destination);
      const snare = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
      const sd = snare.getChannelData(0);
      for (let i = 0; i < snare.length; i++) sd[i] = Math.random() * 2 - 1;
      const hh = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
      const hd = hh.getChannelData(0);
      for (let i = 0; i < hh.length; i++) hd[i] = Math.random() * 2 - 1;
      snareNoiseRef.current = snare;
      hihatNoiseRef.current = hh;
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    isPlayingRef.current = true;
    setIsPlaying(true);
    globalStepRef.current = 0;
    setCurrentChordIdx(0);
    nextStepTimeRef.current = ctxRef.current.currentTime + 0.05;
    timerRef.current = setInterval(scheduler, LOOKAHEAD_MS);
  }, [scheduler, volume]);

  const stop = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setCurrentChordIdx(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const toggle = () => (isPlayingRef.current ? stop() : start());

  useEffect(() => {
    return () => {
      isPlayingRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (ctxRef.current) {
        try { ctxRef.current.close(); } catch (_) {}
        ctxRef.current = null;
      }
    };
  }, []);

  const accentBtn = {
    amber: 'from-amber to-orange-500',
    blue: 'from-blue to-cyan-400',
    purple: 'from-purple-500 to-pink-500',
  }[accent] || 'from-amber to-orange-500';

  const stemBtn = (active, color = 'amber') => active
    ? `bg-${color}/15 border-${color}/40 text-${color}`
    : 'bg-white/[0.02] border-white/10 text-zinc-500 hover:text-zinc-300';

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={toggle}
            className={`w-11 h-11 rounded-full bg-gradient-to-r ${accentBtn} text-black flex items-center justify-center hover:scale-105 transition-transform shrink-0`}
            aria-label={isPlaying ? 'Stop backing band' : 'Start backing band'}
          >
            {isPlaying ? <Square className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t('songs.backingBand', { defaultValue: 'Backing band' })}</span>
            <span className="font-mono text-amber text-lg font-bold truncate">{safeChords[currentChordIdx] || '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {safeChords.map((c, i) => (
            <span
              key={`${c}-${i}`}
              className={`text-[11px] font-mono px-1.5 py-0.5 rounded shrink-0 transition-colors ${
                isPlaying && i === currentChordIdx
                  ? 'bg-amber text-black'
                  : 'bg-white/5 text-zinc-500'
              }`}
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => setDrumsOn(v => !v)}
          className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-xs font-semibold uppercase tracking-wider transition-colors ${stemBtn(drumsOn)}`}
        >
          <Drum className="w-3.5 h-3.5" /> {t('songs.stemDrums', { defaultValue: 'Drums' })}
        </button>
        <button
          onClick={() => setBassOn(v => !v)}
          className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-xs font-semibold uppercase tracking-wider transition-colors ${stemBtn(bassOn)}`}
        >
          <Music className="w-3.5 h-3.5" /> {t('songs.stemBass', { defaultValue: 'Bass' })}
        </button>
        <button
          onClick={() => setPianoOn(v => !v)}
          className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-xs font-semibold uppercase tracking-wider transition-colors ${stemBtn(pianoOn)}`}
        >
          <Piano className="w-3.5 h-3.5" /> {t('songs.stemPiano', { defaultValue: 'Piano' })}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="flex flex-col gap-1">
          <label className="text-zinc-500 uppercase tracking-wider text-[10px]">{t('songs.stemDrums', { defaultValue: 'Drums' })}</label>
          <select
            value={patternKey}
            onChange={(e) => setPatternKey(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 text-zinc-200 rounded px-2 py-1 focus:outline-none focus:border-amber/50"
          >
            {Object.keys(PATTERNS).map(p => (
              <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-zinc-500 uppercase tracking-wider text-[10px]">{t('songs.stemBass', { defaultValue: 'Bass' })}</label>
          <select
            value={bassStyleKey}
            onChange={(e) => setBassStyleKey(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 text-zinc-200 rounded px-2 py-1 focus:outline-none focus:border-amber/50"
          >
            <option value="rootFifth">Root + 5</option>
            <option value="alternating">Alternating</option>
            <option value="walking">Walking</option>
            <option value="pedal">Pedal</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-zinc-500 uppercase tracking-wider text-[10px]">{t('songs.stemPiano', { defaultValue: 'Piano' })}</label>
          <select
            value={pianoStyleKey}
            onChange={(e) => setPianoStyleKey(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 text-zinc-200 rounded px-2 py-1 focus:outline-none focus:border-amber/50"
          >
            <option value="block">Block</option>
            <option value="arpeggio">Arpeggio</option>
            <option value="comping">Comping</option>
            <option value="waltz">Waltz</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-zinc-400">
        <span className="shrink-0">BPM</span>
        <input
          type="range"
          min={40}
          max={240}
          step={1}
          value={bpm}
          onChange={(e) => setBpm(Number(e.target.value))}
          className="flex-1 accent-amber"
        />
        <span className="w-10 text-right shrink-0">{t('drums.volume', { defaultValue: 'Vol' })}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(volume * 100)}
          onChange={(e) => setVolume(Number(e.target.value) / 100)}
          className="w-20 accent-amber"
        />
      </div>
    </div>
  );
};

export default BackingTrack;
